/* eslint-disable @typescript-eslint/no-explicit-any */
import Vue from 'vue';
import Router from 'vue-router';

import GenericRoute from '@dh-io-otb/shared/src/models/utilities/router.model';
import { APPLICATION_URL, ROUTER, TRACKING_ROUTE } from './models/constants';

Vue.use(Router);

const beforeEnter = (_to: GenericRoute, _from: GenericRoute, next: (replace: Record<string, boolean>) => void) => {
  // TODO: check if it is in maintenance mode and if the route is allowed.
  next({ replace: true });
};

const redirectToPage = (to: string, from: string) => {
  const currentPage = window.location.href;
  if (isValidPath(from) && currentPage.includes(from)) {
    const newUrl = currentPage.replace(from, to);
    window.location.assign(newUrl);
  }
};

/**
 * Validates if the given path is a known and safe path.
 * This is necessary to prevent untrusted inputs that could lead to security vulnerabilities
 * such as Open Redirects or Cross-Site Scripting (XSS) attacks.
 * Only paths that are explicitly listed as known and safe are considered valid.
 *
 * @param {string} path - The path to validate.
 * @returns {boolean} - Returns true if the path is a known and safe path, otherwise false.
 */

const isValidPath = (path: string) => {
  const knownPaths = Object.values(APPLICATION_URL);
  return knownPaths.includes(path);
};

const routes: any = [
  {
    path: '*',
    beforeEnter(to: GenericRoute, _from: GenericRoute, next: (route: any) => void) {
      next({ path: ROUTER.DEFINE_LOCATION, query: to.query, replace: true });
    },
  },
  {
    path: ROUTER.MESSAGE,
    component: () => import(/* webpackChunkName: "message" */ './views/message/message.vue'),
    beforeEnter,
    meta: {
      tracking: {
        name: TRACKING_ROUTE.ERROR,
      },
    },
  },
  {
    path: ROUTER.DEEPLINK_LIST,
    component: () => import(/* webpackChunkName: "deeplink-list" */ './views/deeplink-list/deeplink-list.vue'),
    beforeEnter,
    meta: {
      next: ROUTER.DEFINE_LOCATION,
    },
  },
  {
    path: ROUTER.DEFINE_LOCATION,
    component: () => import(/* webpackChunkName: "define-location" */ './views/define-location/define-location.vue'),
    beforeEnter,
    meta: {
      tracking: {
        name: TRACKING_ROUTE.DEFINE_LOCATION,
      },
      headerExpanded: true,
      showHeader: true,
      showSearch: true,
      showBackLink: true,
      next: ROUTER.SCHEDULE,
    },
  },
  {
    path: ROUTER.SCHEDULE,
    component: () => import(/* webpackChunkName: "schedule" */ './views/schedule/schedule.vue'),
    beforeEnter,
    meta: {
      tracking: {
        name: TRACKING_ROUTE.SCHEDULE,
      },
      next: ROUTER.PERSONAL_DATA,
      showHeader: true,
      showSearch: true,
      showBackLink: true,
      previous: ROUTER.DEFINE_LOCATION,
    },
  },
  {
    path: ROUTER.REQUEST,
    component: () => import(/* webpackChunkName: "request" */ './views/request/request.vue'),
    beforeEnter,
    meta: {
      tracking: {
        name: TRACKING_ROUTE.REQUEST,
      },
      next: ROUTER.ONEDOC,
      showHeader: true,
      showSearch: true,
      showHeaderTooltip: true,
      showReturn: true,
      pageTitle: 'header.request.title',
      previous: ROUTER.SCHEDULE, // TODO: Define how to return with request specs
    },
  },
  {
    path: ROUTER.PERSONAL_DATA,
    component: () => import(/* webpackChunkName: "personal-data" */ './views/personal-data/personal-data.vue'),
    beforeEnter,
    meta: {
      tracking: {
        name: TRACKING_ROUTE.PERSONAL_DATA,
      },
      next: ROUTER.ONEDOC,
      showHeader: true,
      showSearch: true,
      showReturn: true,
      previous: ROUTER.SCHEDULE, // TODO: define how to return to schedule route or request
    },
  },
  {
    path: ROUTER.ONEDOC,
    component: () => import(/* webpackChunkName: "onedoc" */ './views/onedoc/onedoc.vue'),
    beforeEnter,
    meta: {
      next: ROUTER.SUMMARY,
      previous: ROUTER.PERSONAL_DATA,
    },
  },
  {
    path: ROUTER.SUMMARY,
    component: () => import(/* webpackChunkName: "summary" */ './views/summary/summary.vue'),
    beforeEnter,
    meta: {
      tracking: {
        name: TRACKING_ROUTE.SUMMARY,
      },
      previous: ROUTER.PERSONAL_DATA,
    },
  },
  // TODO: Cancellation & Reschedule Features
  // Right now it redirects to the old customer journey
  {
    path: ROUTER.CANCELLATION,
    component: () => import(/* webpackChunkName: "cancellation" */ './views/cancellation/cancellation.vue'),
    beforeEnter: () => {
      redirectToPage(APPLICATION_URL.OLD_CUSTOMER_JOURNEY, APPLICATION_URL.NEW_CUSTOMER_JOURNEY);
    },
    meta: {
      tracking: {
        name: TRACKING_ROUTE.CANCELLATION,
      },
    },
  },
  {
    path: ROUTER.RESCHEDULE,
    component: () => import(/* webpackChunkName: "reschedule" */ './views/reschedule/reschedule.vue'),
    beforeEnter: () => {
      redirectToPage(APPLICATION_URL.OLD_CUSTOMER_JOURNEY, APPLICATION_URL.NEW_CUSTOMER_JOURNEY);
    },
    meta: {
      tracking: {
        name: TRACKING_ROUTE.RESCHEDULE,
      },
    },
  },
];

const router = new Router({
  routes,
});

export default router;
